import { useId, useMemo } from "react";

import { useStableRest } from "../../utils/use-stable-rest";
import type {
  FormFieldControlProps,
  FormFieldRootAttributes,
  FormFieldViewModel,
  UseFormFieldOptions,
} from "./types-form-field";

/** ViewModel do FormField: ids, flags e props do controle derivados das opções; as partes leem o `context`; sem JSX. */
export function useFormField({
  id,
  name,
  required = false,
  error,
  help,
  disabled = false,
  labelTone = "primary",
  labelPosition = "top",
  floatVariant = "over",
  size = "md",
  testId,
  className,
  style,
  classNames,
  ...props
}: UseFormFieldOptions & FormFieldRootAttributes = {}): FormFieldViewModel {
  const rest = useStableRest(props);
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const helpId = `${fieldId}-help`;
  const errorId = `${fieldId}-error`;
  // `-field-label` (não `-label`): InputSwitch e Checkbox (com rótulo ao lado) não têm rótulo próprio; o RadioGroup usa `<inputId>-label` para o rótulo de cada item.
  const labelId = `${fieldId}-field-label`;
  const errorText = error ? error : null;
  const helpText = help ? help : null;

  return useMemo(() => {
    const describedBy = [helpText ? helpId : null, errorText ? errorId : null]
      .filter(Boolean)
      .join(" ");
    const controlProps: FormFieldControlProps = {
      id: fieldId,
      "aria-labelledby": labelId,
      ...(name ? { name } : {}),
      ...(errorText ? { "aria-invalid": true as const } : {}),
      ...(describedBy ? { "aria-describedby": describedBy } : {}),
      ...(required ? { "aria-required": true as const } : {}),
      ...(disabled ? { disabled: true as const } : {}),
    };

    return {
      labelTone,
      labelPosition,
      floatVariant: labelPosition === "float" ? floatVariant : undefined,
      size,
      disabled,
      hasError: errorText !== null,
      testId,
      className,
      style,
      rest,
      context: {
        id: fieldId,
        helpId,
        errorId,
        labelId,
        required,
        error: errorText,
        help: helpText,
        hasError: errorText !== null,
        classNames: classNames ?? {},
        controlProps,
        partTestId: (part: string) => (testId ? `${testId}-${part}` : undefined),
      },
    };
  }, [
    fieldId,
    helpId,
    errorId,
    labelId,
    name,
    required,
    errorText,
    helpText,
    disabled,
    labelTone,
    labelPosition,
    floatVariant,
    size,
    testId,
    className,
    style,
    classNames,
    rest,
  ]);
}
